'use client';

import { useState, memo, lazy, Suspense } from 'react';
import { Message, Sender, FlowStep } from './types';
import { ArrowRight, Bot, Check, Sparkles, User, ZoomIn, ChevronUp, ShoppingCart, ExternalLink } from 'lucide-react';
import { DreamBotIcon } from './DreamBotIcon';
import { trackEvent } from './analytics';
import { useKeyboardNavigation } from './useKeyboardNavigation';
import { triggerHapticFeedback } from './useSwipeGesture';
// Lazy load heavy modal components
const ProductImageZoom = lazy(() => import('./ProductImageZoom').then(m => ({ default: m.ProductImageZoom })));
const ProductDetailsExpanded = lazy(() => import('./ProductDetailsExpanded').then(m => ({ default: m.ProductDetailsExpanded })));

export const MessageBubble = memo(function MessageBubble({
  message,
  isLast,
  onReply,
}: {
  message: Message;
  isLast?: boolean;
  onReply?: (value: string, nextStep?: FlowStep, displayText?: string) => void;
}) {
  const isBot = message.sender === Sender.BOT;
  const isProductCard = !!message.productDetails;
  const hasProductRecommendations =
    isBot && Array.isArray(message.productRecommendations) && message.productRecommendations.length > 0;
  const isWideCard = isProductCard || hasProductRecommendations;
  const showActions = isBot && message.quickReplies && message.quickReplies.length > 0 && isLast;
  const [showZoom, setShowZoom] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const normalizeText = (v: any) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');
  const formatProductName = (name: any) => {
    const out = normalizeText(name);
    return out || 'Recommended Product';
  };
  const formatPrice = (price: any) => {
    let out = normalizeText(price);
    if (!out) return 'See Price';

    out = out
      .replace(/\bprice\s*range\s*:\s*/ig, '')
      .replace(/\boriginal\s*price\s*was\s*:\s*/ig, '')
      .replace(/\s*\boriginal\s*price\s*was\b.*$/i, '')
      .replace(/\s*\bprice\s*range\b.*$/i, '');

    out = normalizeText(out);
    if (!out) return 'See Price';
    if (out.length > 28) out = out.slice(0, 28).trim();
    return out;
  };

  // Keyboard navigation for quick reply buttons
  const quickReplyContainerRef = useKeyboardNavigation(
    showActions || false,
    undefined,
    message.quickReplies?.length || 0
  );

  return (
    <div className={`flex w-full mb-4 animate-message-slide-up ${isBot ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`flex flex-col ${isWideCard ? 'w-full' : 'max-w-full md:max-w-[95%]'} ${isBot ? 'items-start' : 'items-end'}`}
      >
        {/* Message Content */}
        <div
          className={`
            relative transition-all duration-300 w-full
            ${message.productDetails ? 'p-0 bg-transparent shadow-none' : 'px-6 py-4'}
            ${!message.productDetails &&
            (isBot
              ? 'rounded-2xl rounded-tl-sm message-bubble bot shadow-lg'
              : 'text-foreground rounded-2xl rounded-tr-sm message-bubble user')
            }
          `}
        >
            {message.productDetails ? (
              <div className="bg-card rounded-2xl overflow-hidden shadow-sm border border-border max-w-sm mx-auto transition-all duration-300 hover:shadow-md hover:border-foreground/20">
                {/* Product Image */}
                <div 
                  className="relative h-48 w-full overflow-hidden bg-muted cursor-zoom-in group/image"
                  onClick={() => {
                    setShowZoom(true);
                    trackEvent('product_image_zoom', { name: message.productDetails?.name });
                  }}
                >
                  <img
                    src={message.productDetails.imageUrl}
                    alt={message.productDetails.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover/image:scale-105"
                    loading="lazy"
                  />
                  {/* Zoom indicator */}
                  <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/10 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover/image:opacity-100 transition-opacity bg-white/90 rounded-full p-2">
                      <ZoomIn size={16} className="text-black" />
                    </div>
                  </div>
                </div>

                {/* Product Content */}
                <div className="p-4">
                  {/* Product Header - Always Visible */}
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4
                      className="text-[16px] leading-[1.35] tracking-[-0.015em] font-semibold text-foreground flex-1"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {formatProductName(message.productDetails.name)}
                    </h4>
                    <div className="text-[16px] font-bold text-foreground tabular-nums shrink-0 whitespace-nowrap">
                      {formatPrice(message.productDetails.price)}
                    </div>
                  </div>

                  {/* Description - Collapsed or Expanded */}
                  {!showDetails ? (
                    <div
                      className="text-[14px] text-muted-foreground leading-[1.6] tracking-[-0.011em] mt-2"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {message.productDetails.description || message.productDetails.reasoning}
                    </div>
                  ) : (
                    <div className="mt-3 space-y-4 animate-message-slide-up">
                      {/* Product Description */}
                      {message.productDetails.description && (
                        <div>
                          <h5 className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground mb-2">
                            Product Description
                          </h5>
                          <p className="text-[14px] text-foreground leading-[1.65] tracking-[-0.011em]">
                            {message.productDetails.description}
                          </p>
                        </div>
                      )}

                      {/* Why We Recommend */}
                      <div>
                        <h5 className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground mb-2">
                          Why We Recommend
                        </h5>
                        <p className="text-[14px] text-foreground leading-[1.65] tracking-[-0.011em]">
                          {message.productDetails.reasoning}
                        </p>
                      </div>

                      {/* Key Features */}
                      <div>
                        <h5 className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground mb-2">
                          Key Features
                        </h5>
                        <div className="space-y-1.5">
                          {message.productDetails.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <Check size={14} className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" strokeWidth={3} />
                              <span className="text-[14px] text-foreground leading-[1.6]">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className="bg-muted/50 rounded-xl p-3 border border-border">
                        <div className="space-y-1.5 text-[13px] text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Check size={12} className="text-green-600 dark:text-green-400" strokeWidth={3} />
                            <span>Free shipping on all orders</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="pt-2">
                        <a
                          href={message.productDetails.url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            trackEvent('add_to_cart_clicked', {
                              product: message.productDetails?.name,
                              price: message.productDetails?.price,
                              source: 'dreambot_chat',
                            });
                          }}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-foreground text-background rounded-xl font-semibold text-[13px] tracking-[-0.01em] hover:opacity-90 active:scale-[0.98] transition-all"
                        >
                          <ShoppingCart size={14} />
                          View on Store
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Toggle Button */}
                  <div className="flex items-center justify-between gap-3 mt-3">
                    {!showDetails && (
                      <div className="flex flex-wrap gap-2 min-w-0">
                        {message.productDetails.features.slice(0, 1).map((feat, i) => (
                          <span
                            key={i}
                            className="px-2.5 py-1 bg-muted text-muted-foreground rounded-lg text-[10px] font-semibold uppercase tracking-[0.06em] flex items-center gap-1.5"
                          >
                            <Check size={10} strokeWidth={3} /> {feat}
                          </span>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={() => {
                        setShowDetails(!showDetails);
                        trackEvent(showDetails ? 'product_details_collapsed' : 'product_details_opened', {
                          name: message.productDetails?.name,
                        });
                      }}
                      className={`shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 bg-white text-black rounded-xl font-semibold text-[13px] tracking-[-0.01em] interactive-scale hover:opacity-90 group/btn ${showDetails ? 'w-full' : '-mr-1'}`}
                    >
                      {showDetails ? (
                        <><ChevronUp size={14} className="group-hover/btn:-translate-y-0.5 transition-transform" /> Hide details</>
                      ) : (
                        <>View details<ArrowRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform" /></>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Avatar and Name inside bubble */}
                <div className={`flex items-center gap-2 mb-2 ${isBot ? 'flex-row' : 'flex-row-reverse'}`}>
                  {isBot ? (
                    <DreamBotIcon size={16} className="shrink-0" />
                  ) : (
                    <User size={16} className="text-muted-foreground dark:text-white shrink-0" />
                  )}
                  <span className="text-[9px] font-bold uppercase tracking-[0.08em] opacity-70">
                    {isBot ? 'DreamBot' : 'You'}
                  </span>
                </div>
                <div className="whitespace-pre-wrap text-[15px] leading-[1.65] font-normal tracking-[-0.011em]">
                  {message.text ? (
                    message.text.split(/\*\*(.*?)\*\*/g).map((part, i) =>
                      i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
                    )
                  ) : (
                    <span className="opacity-50 italic">Processing response...</span>
                  )}
                </div>

                {hasProductRecommendations && (
                  <div className="mt-4 flex flex-col gap-3 w-full">
                    {message.productRecommendations!.slice(0, 3).map((p, idx) => (
                      <div
                        key={`${p.name}-${idx}`}
                        className="bg-card rounded-2xl overflow-hidden shadow-floating border border-foreground/10 transition-all duration-300 w-full"
                      >
                        <div className="relative h-32 w-full overflow-hidden bg-muted">
                          <img
                            src={p.imageUrl}
                            alt={formatProductName(p.name)}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h4
                              className="text-[14px] leading-[1.3] tracking-[-0.01em] font-semibold text-foreground flex-1"
                              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            >
                              {formatProductName(p.name)}
                            </h4>
                            <div className="text-[14px] font-semibold text-foreground tabular-nums shrink-0 whitespace-nowrap">
                              {formatPrice(p.price)}
                            </div>
                          </div>
                          <div
                            className="text-[13px] text-muted-foreground leading-[1.5] tracking-[-0.01em]"
                            style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                          >
                            {p.description || p.reasoning}
                          </div>

                          {Array.isArray(p.features) && p.features.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {p.features.slice(0, 2).map((feat, i) => (
                                <span
                                  key={i}
                                  className="px-2.5 py-1 bg-muted text-muted-foreground rounded-lg text-[10px] font-semibold uppercase tracking-[0.06em] flex items-center gap-1.5"
                                >
                                  <Check size={10} strokeWidth={3} /> {feat}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="pt-3">
                            <a
                              href={p.url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => {
                                trackEvent('open_chat_product_clicked', {
                                  product: p.name,
                                  price: p.price,
                                  source: 'dreambot_chat',
                                });
                              }}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-foreground text-background rounded-xl font-semibold text-[13px] tracking-[-0.01em] hover:opacity-90 active:scale-[0.98] transition-all"
                            >
                              <ShoppingCart size={14} />
                              View on Store
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
        </div>

        {/* Quick Reply Actions */}
        {showActions && (
          <div 
            ref={quickReplyContainerRef}
            role="group"
            aria-label="Quick reply options"
            className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4 w-full animate-message-slide-up" style={{ animationDelay: '0.1s' }}
          >
            {message.quickReplies!.map((action, idx) => (
              <button
                key={idx}
                aria-label={`Quick reply: ${action.label}`}
                onClick={() => {
                  triggerHapticFeedback('light');
                  onReply?.(action.value, action.nextFlowStep, action.label);
                  trackEvent('quick_reply_clicked', { label: action.label });
                }}
                className="group relative px-5 py-3.5 text-left rounded-2xl quick-reply-button response-button transition-all duration-200 active:scale-[0.98]"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[15px] font-medium tracking-[-0.011em] transition-colors text-[inherit]">
                    {action.label}
                  </span>
                  <ArrowRight size={16} className="text-[inherit] opacity-60 group-hover:opacity-95 group-hover:translate-x-0.5 transition-all duration-200" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
